import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { HtmlNotesEditor } from "@/components/ui/HtmlNotesEditor";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  addArtikelInternAnhang,
  deleteArtikelInternAnhang,
  getArtikelDetail,
  getArtikelInternAnhangUrl,
  setArtikelInterneNotizen,
  setArtikelSchutz,
  softDeleteArtikel,
  type ArtikelDetail,
  type ArtikelDetailCore,
  type ArtikelHistorieEintrag,
  type ArtikelInternAnhang,
} from "@/lib/artikel";
import { saveArtikelRabatt, type ArtikelRabattState } from "@/lib/artikelRabatt";
import { saveArtikelZuschlag, type ArtikelZuschlagState } from "@/lib/artikelZuschlag";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";
import { formatDate, formatDateTime } from "@/lib/format";
import { ARTIKEL_ETIKETT_MAX, toArtikelEtikettItem } from "@/lib/artikelEtikett";
import { useArtikelEtikettCart } from "@/lib/artikelEtikettCart";
import { toast } from "@/lib/toast";
import { ArtikelEtikettDialog } from "@/pages/artikel/ArtikelEtikettDialog";
import { IMPORT_FELD_LABELS } from "@/lib/artikelImport";
import { getArtikelColumnLabel } from "@/pages/artikel/artikelColumns";
import {
  ArtikelRabattSection,
  previewEk as previewRabattEk,
  previewRabattPct,
  rabattSelectionToSaveBody,
  type RabattSelection,
} from "@/pages/artikel/ArtikelRabattSection";
import {
  ArtikelZuschlagSection,
  selectionToSaveBody,
  type ZuschlagSelection,
} from "@/pages/artikel/ArtikelZuschlagSection";
import {
  INACTIVE_RABATT,
  INACTIVE_ZUSCHLAG,
  activateRabatt,
  activateZuschlag,
  exclusivePriceSelectionsFromState,
  isRabattActive,
  isZuschlagActive,
} from "@/pages/artikel/artikelPriceSelection";

const STAMMDATEN_KEYS = [
  "hero_nr",
  "hero_base_name",
  "hero_base_description",
  "hero_base_unit_type",
  "hero_base_ean",
  "hero_base_category",
  "hero_base_matchcode",
  "hero_base_manufacturer",
  "hero_base_manufacturer_nr",
  "hero_supply_operator_name",
  "hero_base_supplier_nr",
  "hero_base_manufacturer_type_name",
  "hero_base_delivery_time",
  "hero_base_external_url",
] as const satisfies readonly (keyof ArtikelDetailCore)[];

const TECHNIK_KEYS = ["id"] as const satisfies readonly (keyof ArtikelDetailCore)[];

const AKTION_LABELS: Record<string, string> = {
  angelegt: "Angelegt",
  aktualisiert: "Aktualisiert",
  schutz_geaendert: "Schutz geändert",
  geloescht: "Gelöscht",
};

const QUELLE_LABELS: Record<string, string> = {
  datanorm: "Datanorm-Import",
  excel: "Excel-Import",
  manuell: "Manuell",
  hero: "Hero",
  sonderkondition: "Sonderkondition",
};

const HISTORIE_FELD_LABELS: Record<string, string> = {
  ...IMPORT_FELD_LABELS,
  import_geschuetzt: "Importschutz (Artikel)",
  preis_geschuetzt: "Preisschutz",
  rabatt_modus: "Rabattmodus",
  rabatt_regel_id: "Rabattstaffel",
  zuschlag_modus: "Zuschlagmodus",
  zuschlag_regel_id: "Zuschlagsstaffel",
};

function feldLabel(feld: string): string {
  return HISTORIE_FELD_LABELS[feld] ?? getArtikelColumnLabel(feld);
}

function formatValue(key: string, value: string | number | boolean | null): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  if (key.endsWith("_at")) return formatDateTime(String(value));
  return String(value);
}

function formatMoney(value: number | null): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number(value),
  );
}

function historieWert(value: string | number | boolean | null): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Ja" : "Nein";
  return String(value);
}

function isImage(contentType: string | null | undefined): boolean {
  return Boolean(contentType && contentType.startsWith("image/"));
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface ArtikelDetailDialogProps {
  open: boolean;
  artikelId: number | null;
  onClose: () => void;
  /** Wird gerufen, wenn sich Daten geändert haben (z. B. Schutz-Flags). */
  onChanged?: () => void;
  onEdit?: (artikelId: number) => void;
  onLieferantClick?: (geschaeftspartnerId: number) => void;
  /** Erhöhen, um Detail neu zu laden (z. B. nach Speichern im Edit-Dialog). */
  reloadToken?: number;
}

export function ArtikelDetailDialog({
  open,
  artikelId,
  onClose,
  onChanged,
  onEdit,
  onLieferantClick,
  reloadToken = 0,
}: ArtikelDetailDialogProps) {
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const [detail, setDetail] = useState<ArtikelDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zuschlagState, setZuschlagState] = useState<ArtikelZuschlagState | null>(null);
  const [zuschlagSelection, setZuschlagSelection] = useState<ZuschlagSelection>(INACTIVE_ZUSCHLAG);
  const [rabattState, setRabattState] = useState<ArtikelRabattState | null>(null);
  const [rabattSelection, setRabattSelection] = useState<RabattSelection>(INACTIVE_RABATT);
  const [savingZuschlag, setSavingZuschlag] = useState(false);
  const [savingSchutz, setSavingSchutz] = useState(false);
  const [showTechnik, setShowTechnik] = useState(false);
  const [interneNotizen, setInterneNotizen] = useState("");
  const [etikettOpen, setEtikettOpen] = useState(false);
  const etikettCart = useArtikelEtikettCart();
  const [savingNotizen, setSavingNotizen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingAnhang, setUploadingAnhang] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const reload = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const next = await getArtikelDetail(id);
      setDetail(next);
      const zs = next.zuschlag ?? null;
      const rs = next.rabatt ?? null;
      const exclusive = exclusivePriceSelectionsFromState(rs, zs);
      setZuschlagState(zs);
      setZuschlagSelection(exclusive.zuschlag);
      setRabattState(rs);
      setRabattSelection(exclusive.rabatt);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Artikel konnte nicht geladen werden.");
      setDetail(null);
      setZuschlagState(null);
      setRabattState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && artikelId != null) {
      setDetail(null);
      setShowTechnik(false);
      setInterneNotizen("");
      setZuschlagState(null);
      setRabattState(null);
      void reload(artikelId);
    }
  }, [open, artikelId, reload, reloadToken]);

  useEffect(() => {
    if (detail?.artikel) {
      setInterneNotizen(String(detail.artikel.interne_notizen ?? ""));
    }
  }, [detail?.artikel?.id, detail?.artikel?.interne_notizen]);

  async function handleSaveZuschlag() {
    if (artikelId == null) return;
    const rabattOn = isRabattActive(rabattSelection);
    const zuschlagOn = isZuschlagActive(zuschlagSelection);
    if (rabattOn === zuschlagOn) {
      setError("Bitte entweder einen Rabatt oder einen Zuschlag auswählen.");
      return;
    }
    if (rabattOn) {
      const rabattBody = rabattSelectionToSaveBody(rabattSelection);
      if ("error" in rabattBody) {
        setError(rabattBody.error);
        return;
      }
      setSavingZuschlag(true);
      setError(null);
      try {
        await saveArtikelRabatt(artikelId, rabattBody);
        toast.success("Preise gespeichert.");
        await reload(artikelId);
        onChanged?.();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Preise konnten nicht gespeichert werden.");
      } finally {
        setSavingZuschlag(false);
      }
      return;
    }
    const zuschlagBody = selectionToSaveBody(zuschlagSelection);
    if ("error" in zuschlagBody) {
      setError(zuschlagBody.error);
      return;
    }
    setSavingZuschlag(true);
    setError(null);
    try {
      await saveArtikelZuschlag(artikelId, zuschlagBody);
      toast.success("Preise gespeichert.");
      await reload(artikelId);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Preise konnten nicht gespeichert werden.");
    } finally {
      setSavingZuschlag(false);
    }
  }

  async function toggleSchutz(kind: "import" | "preis", value: boolean) {
    if (artikelId == null) return;
    setSavingSchutz(true);
    setError(null);
    try {
      await setArtikelSchutz(artikelId, {
        importGeschuetzt: kind === "import" ? value : undefined,
        preisGeschuetzt: kind === "preis" ? value : undefined,
      });
      await reload(artikelId);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Schutz konnte nicht geändert werden.");
    } finally {
      setSavingSchutz(false);
    }
  }

  async function handleSoftDelete() {
    if (artikelId == null || !detail?.artikel || detail.artikel.deleted_at) return;
    const label =
      String(detail.artikel.hero_nr || "").trim() ||
      String(detail.artikel.hero_base_name || "").trim() ||
      String(artikelId);
    const ok = window.confirm(
      `Artikel ${label} wirklich löschen?\n\nDer Artikel wird nur ausgeblendet. Stundennachweise und andere Belege, auf denen er bereits verwendet wird, bleiben erhalten.`,
    );
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await softDeleteArtikel(artikelId);
      toast.success("Artikel gelöscht.");
      onChanged?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Artikel konnte nicht gelöscht werden.");
    } finally {
      setDeleting(false);
    }
  }

  async function saveInterneNotizen() {
    if (artikelId == null) return;
    setSavingNotizen(true);
    setError(null);
    try {
      const plain = interneNotizen
        .replace(/<br\s*\/?>/gi, "")
        .replace(/&nbsp;/gi, " ")
        .replace(/<[^>]+>/g, "")
        .trim();
      await setArtikelInterneNotizen(artikelId, plain ? interneNotizen : null);
      await reload(artikelId);
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Notizen konnten nicht gespeichert werden.");
    } finally {
      setSavingNotizen(false);
    }
  }

  async function handleUploadAnhang(file: File) {
    if (artikelId == null) return;
    setUploadingAnhang(true);
    setError(null);
    try {
      await addArtikelInternAnhang(artikelId, file);
      await reload(artikelId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Datei konnte nicht hochgeladen werden.");
    } finally {
      setUploadingAnhang(false);
    }
  }

  async function handleDeleteAnhang(anhang: ArtikelInternAnhang) {
    if (artikelId == null) return;
    setError(null);
    try {
      await deleteArtikelInternAnhang(artikelId, anhang.id);
      await reload(artikelId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anhang konnte nicht gelöscht werden.");
    }
  }

  const artikel = detail?.artikel ?? null;
  const etikettItems = useMemo(
    () => (artikel ? [toArtikelEtikettItem(artikel)] : []),
    [artikel],
  );
  const aktuellerPreis = detail?.preise?.[0] ?? null;
  const name = artikel ? String(artikel.hero_base_name ?? "") : "";
  const listenpreisOverride =
    artikel?.hero_list_price != null && Number(artikel.hero_list_price) > 0
      ? Number(artikel.hero_list_price)
      : aktuellerPreis?.bruttopreis != null && Number(aktuellerPreis.bruttopreis) > 0
        ? Number(aktuellerPreis.bruttopreis)
        : null;
  const rabattPreviewEk = previewRabattEk(
    rabattSelection,
    listenpreisOverride,
    rabattState?.staffeln ?? [],
  );
  const rabattPreviewPct = previewRabattPct(rabattSelection, rabattState?.staffeln ?? []);

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      dirty={interneNotizen !== String(detail?.artikel?.interne_notizen ?? "")}
      title={artikel ? `Artikel ${String(artikel.hero_nr ?? artikel.id)}` : "Artikel"}
      description={name || undefined}
      size="2xl"
      bodyClassName="overflow-y-auto max-h-[75vh]"
      footer={
        artikel && artikelId != null ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div>
              {canSeePrices && !artikel.deleted_at ? (
                <Button
                  type="button"
                  variant="danger"
                  loading={deleting}
                  onClick={() => void handleSoftDelete()}
                >
                  Löschen
                </Button>
              ) : null}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                const item = toArtikelEtikettItem(artikel);
                const action = etikettCart.toggle(item);
                if (action === "full") {
                  toast.error(`Der Etikettenkorb ist voll (max. ${ARTIKEL_ETIKETT_MAX}).`);
                  return;
                }
                toast.success(
                  action === "added"
                    ? "Artikel in den Etikettenkorb gelegt."
                    : "Artikel aus dem Etikettenkorb entfernt.",
                );
              }}
            >
              {etikettCart.ids.has(artikel.id) ? "Aus dem Korb" : "In den Etikettenkorb"}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEtikettOpen(true)}>
              Etikett drucken
            </Button>
            {onEdit ? (
              <Button type="button" variant="secondary" onClick={() => onEdit(artikelId)}>
                Bearbeiten
              </Button>
            ) : null}
            </div>
          </div>
        ) : undefined
      }
    >
      <div className="space-y-5">
        {error && <Alert variant="error">{error}</Alert>}

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}

        {artikel && !loading && (
          <>
            {artikel.deleted_at ? (
              <Alert variant="warning">
                Dieser Artikel ist gelöscht und erscheint nicht mehr in der Suche. Bestehende
                Belege, zum Beispiel Stundennachweise, können ihn weiter verwenden.
              </Alert>
            ) : null}
            {/* Schutz */}
            <section className="rounded-ios border border-separator bg-card-elevated px-4 py-3">
              <h3 className="text-sm font-semibold text-label">Import-Schutz</h3>
              <p className="mt-1 text-xs text-label-secondary">
                Geschützte Artikel bzw. Preise werden bei Datanorm- und Excel-Importen nicht
                überschrieben (z.&nbsp;B. bei ausgehandelten Sonderkonditionen).
              </p>
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-label">
                  <input
                    type="checkbox"
                    className="rounded border-separator"
                    checked={Boolean(artikel.import_geschuetzt)}
                    disabled={savingSchutz}
                    onChange={(e) => void toggleSchutz("import", e.target.checked)}
                  />
                  Ganzen Artikel schützen
                </label>
                <label
                  className={clsx(
                    "flex items-center gap-2 text-sm text-label",
                    !aktuellerPreis && "opacity-50",
                  )}
                >
                  <input
                    type="checkbox"
                    className="rounded border-separator"
                    checked={Boolean(aktuellerPreis?.geschuetzt)}
                    disabled={savingSchutz || !aktuellerPreis}
                    onChange={(e) => void toggleSchutz("preis", e.target.checked)}
                  />
                  Nur aktuellen Preis schützen
                </label>
              </div>
            </section>

            {/* Interne Notizen (werden nie durch Import überschrieben) */}
            <section className="rounded-ios border border-separator bg-card-elevated px-4 py-3">
              <h3 className="text-sm font-semibold text-label">Interne Notizen</h3>
              <p className="mt-1 text-xs text-label-secondary">
                Nur für das Büro sichtbar. Diese Notizen und Anhänge werden bei Datanorm- und
                Excel-Importen nicht verändert.
              </p>
              <HtmlNotesEditor
                className="mt-3"
                minRows={4}
                placeholder="Interne Hinweise, Lagerort, Besonderheiten …"
                value={interneNotizen}
                onChange={setInterneNotizen}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={savingNotizen}
                  onClick={() => void saveInterneNotizen()}
                >
                  {savingNotizen ? "Speichern …" : "Notizen speichern"}
                </Button>
                {artikel.interne_notizen_updated_at ? (
                  <span className="text-xs text-label-tertiary">
                    Zuletzt{" "}
                    {formatDateTime(String(artikel.interne_notizen_updated_at))}
                    {artikel.interne_notizen_updated_by
                      ? ` · ${String(artikel.interne_notizen_updated_by)}`
                      : ""}
                  </span>
                ) : null}
              </div>

              <div className="mt-4 space-y-2">
                <div className="text-sm font-medium text-label">Bilder / Dateien</div>
                {(detail?.interne_anhaenge?.length ?? 0) > 0 && (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {detail!.interne_anhaenge.map((att) => {
                      const url = getArtikelInternAnhangUrl(artikel.id, att.id);
                      return (
                        <div
                          key={att.id}
                          className="group relative overflow-hidden rounded-ios border border-separator-opaque bg-fill-tertiary"
                        >
                          {isImage(att.content_type) ? (
                            <a href={url} target="_blank" rel="noreferrer">
                              <img
                                src={url}
                                alt={att.filename}
                                className="aspect-square w-full object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="flex aspect-square w-full flex-col items-center justify-center gap-1 p-2 text-center text-xs text-label-secondary"
                            >
                              <span className="line-clamp-3 break-all">{att.filename}</span>
                              <span className="text-label-tertiary">{formatFileSize(att.size_bytes)}</span>
                            </a>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleDeleteAnhang(att)}
                            className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100"
                            aria-label="Anhang löschen"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <label className="inline-flex cursor-pointer items-center gap-2 rounded-ios border border-dashed border-separator-opaque px-3 py-2 text-sm text-label-secondary transition hover:border-accent hover:text-accent">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="sr-only"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    disabled={uploadingAnhang}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (file) void handleUploadAnhang(file);
                    }}
                  />
                  {uploadingAnhang ? "Wird hochgeladen …" : "Datei hinzufügen"}
                </label>
              </div>
            </section>

            {/* Stammdaten */}
            <section>
              <h3 className="text-sm font-semibold text-label">Stammdaten</h3>
              <dl className="mt-2 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                {STAMMDATEN_KEYS.map((key) => (
                  <div key={key} className="flex gap-2">
                    <dt className="w-36 shrink-0 text-label-tertiary">{getArtikelColumnLabel(key)}</dt>
                    <dd className="min-w-0 break-words text-label">
                      {key === "hero_supply_operator_name" && detail?.lieferant?.id ? (
                        <button
                          type="button"
                          className="font-medium text-accent hover:underline"
                          onClick={() => onLieferantClick?.(detail.lieferant!.id)}
                        >
                          {formatValue(key, artikel[key] ?? null)}
                        </button>
                      ) : (
                        formatValue(key, artikel[key] ?? null)
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
              <button
                type="button"
                className="mt-2 text-xs font-medium text-accent hover:underline"
                onClick={() => setShowTechnik((v) => !v)}
              >
                {showTechnik ? "Technische Felder ausblenden" : "Technische Felder anzeigen"}
              </button>
              {showTechnik && (
                <dl className="mt-2 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  {TECHNIK_KEYS.map((key) => (
                    <div key={key} className="flex gap-2">
                      <dt className="w-36 shrink-0 text-label-tertiary">
                        {getArtikelColumnLabel(key)}
                      </dt>
                      <dd className="min-w-0 break-words font-mono text-xs text-label">
                        {formatValue(key, artikel[key] ?? null)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </section>

            {/* Identifikatoren */}
            {(detail?.identifikatoren?.length ?? 0) > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-label">Externe Artikelnummern</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {detail!.identifikatoren.map((ident) => (
                    <span
                      key={ident.id}
                      className="rounded-full bg-fill-tertiary px-3 py-1 font-mono text-xs text-label"
                      title={`Quelle: ${QUELLE_LABELS[ident.quelle] ?? ident.quelle}`}
                    >
                      {ident.lieferant ? (
                        ident.lieferant_geschaeftspartner_id ? (
                          <>
                            <button
                              type="button"
                              className="font-sans font-medium text-accent hover:underline"
                              onClick={() =>
                                onLieferantClick?.(ident.lieferant_geschaeftspartner_id!)
                              }
                            >
                              {ident.lieferant}
                            </button>
                            {": "}
                          </>
                        ) : (
                          <span>{ident.lieferant}: </span>
                        )
                      ) : null}
                      {ident.wert}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Preise – nur Büro/App-Admins */}
            {canSeePrices ? (
            <section>
              <div className="space-y-6 rounded-ios border border-separator bg-card-elevated px-4 py-3">
                <ArtikelRabattSection
                  state={rabattState}
                  selection={rabattSelection}
                  onChange={(next) => {
                    const exclusive = activateRabatt(next);
                    setRabattSelection(exclusive.rabatt);
                    setZuschlagSelection(exclusive.zuschlag);
                  }}
                  disabled={savingZuschlag}
                  listenpreisOverride={listenpreisOverride}
                />
                <ArtikelZuschlagSection
                  state={zuschlagState}
                  selection={zuschlagSelection}
                  onChange={(next) => {
                    const exclusive = activateZuschlag(next);
                    setRabattSelection(exclusive.rabatt);
                    setZuschlagSelection(exclusive.zuschlag);
                  }}
                  disabled={savingZuschlag}
                  ekOverride={rabattPreviewEk ?? (aktuellerPreis?.nettopreis != null ? Number(aktuellerPreis.nettopreis) : null)}
                  listenpreis={listenpreisOverride}
                  rabatt={rabattPreviewPct ?? aktuellerPreis?.rabatt}
                />
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={() => void handleSaveZuschlag()}
                    disabled={savingZuschlag}
                  >
                    {savingZuschlag ? <Spinner className="h-4 w-4" /> : "Preise speichern"}
                  </Button>
                </div>
              </div>

              <h3 className="mt-4 text-sm font-semibold text-label">Preise</h3>
              {(artikel.hero_default_sales_net_price != null ||
                artikel.hero_list_price != null ||
                artikel.hero_base_price != null ||
                artikel.hero_vat_percent != null) && (
                <dl className="mt-2 grid gap-x-6 gap-y-1.5 rounded-ios border border-separator bg-card-elevated px-3 py-2 text-sm sm:grid-cols-2">
                  <div className="flex gap-2">
                    <dt className="w-36 shrink-0 text-label-tertiary">VK netto (Hero)</dt>
                    <dd className="font-mono tabular-nums text-label">
                      {artikel.hero_default_sales_net_price != null
                        ? `${formatMoney(artikel.hero_default_sales_net_price)} €`
                        : "—"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-36 shrink-0 text-label-tertiary">Listenpreis (Hero)</dt>
                    <dd className="font-mono tabular-nums text-label">
                      {artikel.hero_list_price != null
                        ? `${formatMoney(artikel.hero_list_price)} €`
                        : "—"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-36 shrink-0 text-label-tertiary">Basispreis (Hero)</dt>
                    <dd className="font-mono tabular-nums text-label">
                      {artikel.hero_base_price != null
                        ? `${formatMoney(artikel.hero_base_price)} €`
                        : "—"}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-36 shrink-0 text-label-tertiary">MwSt (Hero)</dt>
                    <dd className="font-mono tabular-nums text-label">
                      {artikel.hero_vat_percent != null
                        ? `${formatMoney(artikel.hero_vat_percent)} %`
                        : "—"}
                    </dd>
                  </div>
                </dl>
              )}
              {(detail?.preise?.length ?? 0) === 0 ? (
                <p className="mt-2 text-sm text-label-tertiary">Keine Preise hinterlegt.</p>
              ) : (
                <div className="mt-2 overflow-x-auto rounded-ios border border-separator">
                  <table className="w-full min-w-[56rem] text-sm">
                    <thead className="bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
                      <tr>
                        <th className="px-3 py-2 text-right font-semibold">Listenpreis Netto</th>
                        <th className="px-3 py-2 text-right font-semibold">EK Netto</th>
                        <th className="px-3 py-2 text-right font-semibold">Aufschlag %</th>
                        <th className="px-3 py-2 text-right font-semibold">Rabatt %</th>
                        <th className="px-3 py-2 text-right font-semibold">VK Netto</th>
                        <th className="px-3 py-2 text-left font-semibold">Einheit</th>
                        <th className="px-3 py-2 text-right font-semibold">VPE</th>
                        <th className="px-3 py-2 text-left font-semibold">Gültig bis</th>
                        <th className="px-3 py-2 text-left font-semibold">Quelle</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-separator bg-card">
                      {detail!.preise.map((p, idx) => {
                        const listenpreis =
                          artikel.hero_list_price != null && Number(artikel.hero_list_price) > 0
                            ? Number(artikel.hero_list_price)
                            : null;
                        const ek = p.nettopreis != null ? Number(p.nettopreis) : null;
                        const vk = p.verkaufspreis != null ? Number(p.verkaufspreis) : null;
                        const aufschlag =
                          p.aufschlag != null
                            ? Number(p.aufschlag)
                            : ek != null && ek > 0 && vk != null
                              ? Math.round(((vk - ek) / ek) * 10000) / 100
                              : null;
                        return (
                          <tr key={p.id} className={clsx(idx === 0 && "bg-accent-soft/40")}>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {listenpreis != null ? formatMoney(listenpreis) : "—"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {ek != null ? formatMoney(ek) : "—"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {aufschlag != null ? formatMoney(aufschlag) : "—"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {p.rabatt != null ? formatMoney(p.rabatt) : "—"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {vk != null ? formatMoney(vk) : "—"}
                            </td>
                            <td className="px-3 py-2">{p.einheit ?? "—"}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">
                              {p.preismenge != null ? String(p.preismenge) : "—"}
                            </td>
                            <td className="px-3 py-2">
                              {p.gueltig_bis ? formatDate(p.gueltig_bis) : "—"}
                            </td>
                            <td className="px-3 py-2">
                              {p.preis_quelle ? QUELLE_LABELS[p.preis_quelle] ?? p.preis_quelle : "—"}
                            </td>
                            <td className="px-3 py-2">
                              <span className="flex flex-wrap gap-1">
                                {idx === 0 &&
                                  (p.gueltig_bis &&
                                  new Date(`${String(p.gueltig_bis).slice(0, 10)}T12:00:00`) <
                                    new Date(new Date().toDateString()) ? (
                                    <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-medium text-status-orange">
                                      Abgelaufen
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
                                      Aktuell
                                    </span>
                                  ))}
                                {p.geschuetzt && (
                                  <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-xs font-medium text-status-orange">
                                    Geschützt
                                  </span>
                                )}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            ) : null}

            {/* Historie */}
            <section>
              <h3 className="text-sm font-semibold text-label">Historie</h3>
              {(detail?.historie?.length ?? 0) === 0 ? (
                <p className="mt-2 text-sm text-label-tertiary">
                  Noch keine Änderungen dokumentiert.
                </p>
              ) : (
                <ol className="mt-2 space-y-3">
                  {detail!.historie.map((eintrag) => (
                    <HistorieItem key={eintrag.id} eintrag={eintrag} />
                  ))}
                </ol>
              )}
            </section>
          </>
        )}
      </div>
    </Dialog>
    <ArtikelEtikettDialog
      open={etikettOpen && open}
      onClose={() => setEtikettOpen(false)}
      items={etikettItems}
    />
    </>
  );
}

function HistorieItem({ eintrag }: { eintrag: ArtikelHistorieEintrag }) {
  const [expanded, setExpanded] = useState(false);
  const aenderungen = eintrag.aenderungen ?? [];
  const visible = expanded ? aenderungen : aenderungen.slice(0, 4);

  return (
    <li className="rounded-ios border border-separator bg-card px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-sm font-medium text-label">
          {AKTION_LABELS[eintrag.aktion] ?? eintrag.aktion}
          {eintrag.quelle ? (
            <span className="ml-2 rounded-full bg-fill-tertiary px-2 py-0.5 text-xs font-normal text-label-secondary">
              {QUELLE_LABELS[eintrag.quelle] ?? eintrag.quelle}
            </span>
          ) : null}
        </span>
        <span className="text-xs text-label-tertiary">
          {formatDateTime(eintrag.geaendert_at)}
          {eintrag.geaendert_von ? ` · ${eintrag.geaendert_von}` : ""}
        </span>
      </div>
      {visible.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs text-label-secondary">
          {visible.map((a, i) => (
            <li key={i}>
              <span className="font-medium">{feldLabel(a.feld)}</span>
              {": "}
              {a.alt != null && a.alt !== "" ? (
                <>
                  <span className="line-through opacity-70">{historieWert(a.alt)}</span> →{" "}
                </>
              ) : null}
              <span className="text-label">{historieWert(a.neu)}</span>
            </li>
          ))}
        </ul>
      )}
      {aenderungen.length > 4 && (
        <button
          type="button"
          className="mt-1 text-xs font-medium text-accent hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Weniger anzeigen" : `Alle ${aenderungen.length} Änderungen anzeigen`}
        </button>
      )}
    </li>
  );
}
